/**
 * The plain-data half of `MathText` (ADR 0132 §1), kept apart from the renderer
 * so a generator, a present-mode card or a recent-worksheets line can use the
 * runs without pulling `@react-pdf/renderer` in (`bundleBoundary.test.js`).
 *
 * A run is plain data, so it crosses the PDF worker boundary (ADR 0052).
 */
export type MathRun =
    | { text: string } // ordinary text
    | { sup: string } // a raised exponent: '5', '-3', '12'
    | { root: string; index?: 3 }; // a radical over its radicand; index 3 for a cube root

const SUPERSCRIPT_CHARACTERS: Record<string, string> = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    '-': '⁻',
};

/** `12` -> `¹²`, `-3` -> `⁻³`. A character with no superscript form is kept as it is. */
function superscript(value: string): string {
    return Array.from(value)
        .map((character) => SUPERSCRIPT_CHARACTERS[character] ?? character)
        .join('');
}

/**
 * The same runs as one Unicode string for a screen: `√49`, `∛64`, `10⁻³`.
 *
 * Never passed to a PDF `Text`. Helvetica has no `√`, `∛` or `⁻` (ADR 0070), and
 * `PdfGlyphs.test.tsx` fails if one gets there.
 */
export function mathPlain(runs: MathRun[]): string {
    return runs
        .map((run) => {
            if ('text' in run) {
                return run.text;
            }
            if ('sup' in run) {
                return superscript(run.sup);
            }
            return `${run.index === 3 ? '∛' : '√'}${run.root}`;
        })
        .join('');
}
